import { supabase } from '../client';

const edgeBase = (): string => {
  const u = import.meta.env.VITE_SUPABASE_URL;
  if (!u) return '';
  return `${String(u).replace(/\/$/, '')}/functions/v1`;
};

export type CareerJobRow = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  skills_required: string[] | null;
  experience_min: number | null;
  experience_max: number | null;
  client_name: string;
  career_seo_summary?: string | null;
};

export type CareerApplyResult = {
  ok: boolean;
  application_id?: string;
  duplicate?: boolean;
  track_token?: string;
  error?: string;
  detail?: string;
};

export type CareerApplicationPublicStatus =
  | { ok: true; org_name: string; org_slug: string; job_title: string; status: string; submitted_at: string }
  | { ok: false; error: string };

export const careerService = {
  async getOrgPublic(orgSlug: string): Promise<{ org_name: string; slug: string } | null> {
    const { data, error } = await supabase.rpc('ggj_b2b_career_org_public', { p_org_slug: orgSlug });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : data != null ? [data] : [];
    const row = rows[0] as { org_name?: string; slug?: string } | undefined;
    if (!row?.org_name || !row?.slug) return null;
    return { org_name: row.org_name, slug: row.slug };
  },

  async listPublishedJobs(orgSlug: string): Promise<CareerJobRow[]> {
    const { data, error } = await supabase.rpc('ggj_b2b_career_list_jobs', { p_org_slug: orgSlug });
    if (error) throw error;
    return (data ?? []) as CareerJobRow[];
  },

  async apply(payload: {
    orgSlug: string;
    jobId: string;
    fullName: string;
    email: string;
    phone?: string;
    coverText?: string;
    resumeUrl?: string;
    /** Path from uploadResumeForCareerApply (ggj_b2b-candidate-resumes). */
    resumeStoragePath?: string;
  }): Promise<CareerApplyResult> {
    const { data, error } = await supabase.rpc('ggj_b2b_career_apply', {
      p_org_slug: payload.orgSlug,
      p_job_id: payload.jobId,
      p_full_name: payload.fullName,
      p_email: payload.email,
      p_phone: payload.phone ?? null,
      p_cover_text: payload.coverText ?? null,
      p_resume_url: payload.resumeUrl ?? null,
      p_resume_storage_path: payload.resumeStoragePath ?? null,
    });
    if (error) throw error;
    return (data ?? { ok: false, error: 'empty' }) as CareerApplyResult;
  },

  async getApplicationPublicStatus(token: string): Promise<CareerApplicationPublicStatus> {
    const { data, error } = await supabase.rpc('ggj_b2b_career_application_public', {
      p_token: token,
    });
    if (error) throw error;
    const row = (data ?? { ok: false, error: 'empty' }) as Record<string, unknown>;
    if (row.ok === true) {
      return {
        ok: true,
        org_name: String(row.org_name ?? ''),
        org_slug: String(row.org_slug ?? ''),
        job_title: String(row.job_title ?? ''),
        status: String(row.status ?? ''),
        submitted_at: String(row.submitted_at ?? ''),
      };
    }
    return { ok: false, error: String(row.error ?? 'unknown') };
  },

  /** §9.11 — upload resume via Edge (service role); returns storage path for apply(). */
  async uploadResumeForCareerApply(orgSlug: string, jobId: string, file: File): Promise<string> {
    const base = edgeBase();
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!base || !key) throw new Error('Career upload is not configured (Supabase URL / anon key).');

    const form = new FormData();
    form.set('org_slug', orgSlug);
    form.set('job_id', jobId);
    form.set('file', file);

    const res = await fetch(`${base}/ggj_b2b_career-resume-upload`, {
      method: 'POST',
      headers: { apikey: key },
      body: form,
    });
    const json = (await res.json()) as { ok?: boolean; storage_path?: string; error?: string; detail?: string };
    if (!res.ok || !json.ok || !json.storage_path) {
      throw new Error(json.detail || json.error || `Upload failed (${res.status})`);
    }
    return json.storage_path;
  },

  /** §9.11 — queue confirmation email (Resend) when secrets are configured. */
  async requestCareerConfirmationEmail(applicationId: string, email: string): Promise<{ ok: boolean; skipped?: boolean }> {
    const base = edgeBase();
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!base || !key) return { ok: false };

    const res = await fetch(`${base}/ggj_b2b_career-confirmation-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: key,
      },
      body: JSON.stringify({ application_id: applicationId, email }),
    });
    const json = (await res.json()) as { ok?: boolean; skipped?: boolean };
    if (!res.ok) return { ok: false };
    return { ok: !!json.ok, skipped: json.skipped };
  },
};
